import {formatCell} from '../csv.ts';

export class TimeSeries<T> {

  static deserialize<T>(
    s: string,
    tDeserializer: (s: string) => T,
  ): TimeSeries<T> {
    const j = JSON.parse(s) as {name: string; data: string[]};

    return new TimeSeries<T>(
      j.name,
      j.data.map(
        ps => {
          const j = JSON.parse(ps) as {year: number; value: string};

          return {
            year: j.year,
            value: tDeserializer(j.value),
          } as TimeSeries.Entry<T>;
        },
      ),
    );
  }

  readonly _name: string;
  readonly _data: Array<TimeSeries.Entry<T>>;

  constructor(
    name: string,
    data: Array<TimeSeries.Entry<T>>,

  ) {
    this._name = name;
    this._data = data;
  }

  // Does not mutate this or other.
  // Throws if the names of this and other differ.
  // Returns a new TimeSeries<T>.
  merge(
    other: TimeSeries<T>,
    payloadMerger: (t1: T, t2: T)=>T,
  ): TimeSeries<T> {
    if (this.name !== other.name) {
      throw 'Cannot merge TimeSeries<T> with inconsistent names.';
    }

    const lookup = new Map<number, T>();

    const addToLookup = (entry: TimeSeries.Entry<T>) => {
      const existing = lookup.get(entry.year);
      if (existing !== undefined) {
        lookup.set(entry.year, payloadMerger(existing, entry.value));
      } else {
        lookup.set(entry.year, entry.value);
      }
    };

    this._data.forEach(addToLookup);
    other._data.forEach(addToLookup);

    const mergedData: Array<TimeSeries.Entry<T>> = Array.from(lookup.entries())
      .map(([year, value]) => ({ year, value }))
      .sort((a, b) => a.year - b.year);

    return new TimeSeries<T>(this._name, mergedData);
  }

  get name(): string {
    return this._name;
  }

  get data(): Array<{year: number; value: T}> {
    return this._data;
  }

  serialize(tSerializer: (t: T) => string): string {
    return JSON.stringify({
      name: this._name,
      data: this._data.map(
        p => JSON.stringify({
          year: p.year,
          value: tSerializer(p.value),
        }),
      ),
    });
  }
}

export namespace TimeSeries {
  export interface Entry<T> {
    year: number;
    value: T;
  };
}

export class ColoredSeries<T> extends TimeSeries<T> {
  static deserialize<T>(
    s: string,
    tDeserializer: (s: string) => T,
  ): TimeSeries<T> {
    const j = JSON.parse(s) as {name: string; data: string[]; color: string};

    return new ColoredSeries<T>(
      j.name,
      j.data.map(
        ps => {
          const j = JSON.parse(ps) as {year: number; value: string};

          return {
            year: j.year,
            value: tDeserializer(j.value),
          };
        },
      ),
      j.color,
    );
  }

  _color: string;

  constructor(
    name: string,
    data: Array<{year: number; value: T}>,
    color: string,
  ) {
    super(name, data);
    this._color = color;
  }

  get color(): string {
    return this._color;
  }

  serialize(tSerializer: (t: T) => string): string {
    return JSON.stringify({
      name: this._name,
      data: this._data.map(
        p => JSON.stringify({
          year: p.year,
          value: tSerializer(p.value),
        }),
      ),
      color: this._color,
    });
  }

  // Return a new ColoredSeries with trailing zero-value data points removed.
  trimRight(): ColoredSeries<T> {
    let maxIndex = this._data.length;
    for (let i = this._data.length - 1; i >= 0; i--) {
      if (this._data[i].value !== (0 as unknown as T)) {
        break;
      }
      maxIndex = i;
    }

    return new ColoredSeries<T>(
      this._name,
      this._data.slice(0, maxIndex),
      this._color,
    );
  }
}

export class TimeSeriesS<T> {
  static deserialize<T>(
    s: string,
    tDeserializer: (s: string) => T,
  ): TimeSeriesS<T> {
    const j = JSON.parse(s) as string[];
    const map = new Map<string, TimeSeries<T>>();

    for (const tss of j) {
      const ts = TimeSeries.deserialize<T>(tss, tDeserializer);
      if (map.has(ts.name)) {
        throw new Error(`Wire format violation: duplicate TimeSeries name '${ts.name}'`);
      }
      map.set(ts.name, ts);
    }
    return new TimeSeriesS<T>(map);
  }

  private readonly _seriesMap: Map<string, TimeSeries<T>>;

  constructor(data: Map<string, TimeSeries<T>> | Array<TimeSeries<T>>) {
    if (Array.isArray(data)) {
      this._seriesMap = new Map();
      for (const ts of data) {
        if (this._seriesMap.has(ts.name)) {
          throw new Error(`Constructor violation: duplicate name '${ts.name}'`);
        }
        this._seriesMap.set(ts.name, ts);
      }
    } else {
      this._seriesMap = data;
    }
  }

  /**
   * Merges two collections. If a series exists in both, they are merged.
   */
  merge(other: TimeSeriesS<T>, payloadMerger: (t1: T, t2: T) => T): TimeSeriesS<T> {
    const newMap = new Map(this._seriesMap);

    for (const [name, otherSeries] of other._seriesMap) {
      const existing = newMap.get(name);
      newMap.set(name, existing ? existing.merge(otherSeries, payloadMerger) : otherSeries);
    }

    return new TimeSeriesS<T>(newMap);
  }

  serialize(tSerializer: (t: T) => string): string {
    return JSON.stringify(
      Array.from(this._seriesMap.values()).map(ts => ts.serialize(tSerializer))
    );
  }

  get(name: string): TimeSeries<T> | undefined {
    return this._seriesMap.get(name);
  }

  get data(): TimeSeries<T>[] {
    return Array.from(this._seriesMap.values());
  }
}

// Convert AccountHistory to a CSV string.
// First column is year, ordered in numerical ascending order.
// Subsequent columns are discovered from AssetTypes in the history,
// ordered alphabetically, resulting in a grid of year vs AssetType.
export function toCsv(h: Array<TimeSeries<number>>): string {
  const by_name_year: Map<string, Map<number, number>> = new Map<string, Map<number, number>>();

  for (const ts of h) {
    const by_year = new Map<number, number>();
    by_name_year.set(ts.name, by_year);
    for (const pt of ts.data) {
      by_year.set(pt.year, pt.value);
    }
  }

  const names: string[] = [...by_name_year.keys()].sort();

  /* eslint @typescript-eslint/require-array-sort-compare: "off" */
  const years: number[] = [
    ...new Set(
      h.flatMap(
        ts => ts.data.map(
          p => p.year,
        ),
      ),
    ),
  ].sort();

  const headerCells = ['YEAR', ...names];
  const cells: string[][] = [headerCells];

  for (const y of years) {
    const rowCells = [y.toString()];
    for (const t of names) {
      const amount: number = by_name_year?.get(t)?.get(y) ?? 0;
      rowCells.push(amount.toString());
    }

    cells.push(rowCells);
  }

  const formattedCells = cells.map(rowCells => rowCells.map(cell => formatCell(cell)));
  const rows: string[] = formattedCells.map(rowCells => rowCells.join(','));
  const result: string = rows.join('\n');
  return result;
}
